import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Notice, inputCls } from "@/dashboard/ui";
import { Sparkles, CheckCircle2, Archive, History } from "lucide-react";

const PROFILE_FIELDS = [
  ["business_name", "Business name"], ["industry", "Category / industry"],
  ["location", "Location & service area"], ["website", "Website URL"],
  ["services", "Main products or services"], ["usps", "Unique selling points"],
  ["ideal_customer", "Ideal customer"], ["customer_location", "Target customer location"],
  ["competitors", "Main competitors (if known)"], ["brand_voice", "Brand voice"],
  ["monthly_budget", "Monthly marketing budget (e.g. £200)"],
  ["offers", "Special offers or promotions"], ["additional_info", "Anything else the AI should know"],
];

const GOAL_OPTIONS = ["Get more leads", "Increase bookings", "Increase website traffic", "Improve local awareness", "Grow social media presence", "Increase repeat customers"];
const CHANNEL_OPTIONS = ["Instagram", "Facebook", "Google Business Profile", "Email marketing", "Website & SEO", "Google Ads", "Meta Ads"];

const SECTION_LABELS = {
  overview: "A. Business Overview", audience: "B. Target Audience", positioning: "C. Brand Positioning",
  channels: "D. Recommended Marketing Channels", pillars: "E. Content Pillars",
  plan_30_day: "F. 30-Day Marketing Action Plan", lead_generation: "G. Lead Generation Strategy",
  budget: "H. Budget Recommendations", metrics: "I. Success Metrics",
};
const SECTION_ORDER = Object.keys(SECTION_LABELS);

function ChipGroup({ options, values, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => {
        const on = values.includes(o);
        return (
          <button
            key={o} type="button" data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onChange(on ? values.filter((v) => v !== o) : [...values, o])}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-600 hover:border-neutral-400"}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function StrategyPanel() {
  const [profile, setProfile] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [current, setCurrent] = useState(null);   // full strategy doc being viewed/edited
  const [drafts, setDrafts] = useState(null);      // edited section texts
  const [dirty, setDirty] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [showList, setShowList] = useState(false);

  const load = async () => {
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/marketing/profile"), api.get("/marketing/strategies"),
      ]);
      setProfile({ goals: [], channels: [], ...p.profile });
      setStrategies(s.strategies);
      const approved = s.strategies.find((x) => x.status === "approved") || s.strategies[0];
      if (approved && !current) openStrategy(approved.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openStrategy = async (id) => {
    try {
      const { data } = await api.get(`/marketing/strategies/${id}`);
      setCurrent(data.strategy);
      setDrafts({ ...data.strategy.sections });
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.put("/marketing/profile", profile);
      setProfile({ goals: [], channels: [], ...data.profile });
      toast.success("Marketing profile saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/marketing/strategies/generate");
      toast.success("Strategy generated — review and edit it below");
      setCurrent(data.strategy);
      setDrafts({ ...data.strategy.sections });
      setDirty(false);
      const { data: s } = await api.get("/marketing/strategies");
      setStrategies(s.strategies);
    } catch (e) {
      toast.error(formatApiError(e)); // profile + previous strategies untouched
    } finally {
      setGenerating(false);
    }
  };

  const saveStrategy = async () => {
    setSavingStrategy(true);
    try {
      const { data } = await api.put(`/marketing/strategies/${current.id}`, { sections: drafts, version: current.version });
      setCurrent(data);
      setDrafts({ ...data.sections });
      setDirty(false);
      toast.success("Strategy saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingStrategy(false);
    }
  };

  const approve = async () => {
    if (dirty) { toast.error("Save your edits before approving"); return; }
    try {
      const { data } = await api.post(`/marketing/strategies/${current.id}/approve`);
      setCurrent(data);
      toast.success("Strategy approved — no posts, emails or ads are sent automatically");
      const { data: s } = await api.get("/marketing/strategies");
      setStrategies(s.strategies);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!profile) return <p className="text-sm text-neutral-400">Loading…</p>;
  const approved = strategies.find((s) => s.status === "approved");

  return (
    <div className="space-y-6" data-testid="strategy-panel">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Step 1 — profile */}
        <Card className="p-6" data-testid="strategy-profile-card">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Step 1 — Business marketing profile</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {PROFILE_FIELDS.map(([key, label]) => (
              <label key={key} className={["services", "usps", "ideal_customer", "additional_info", "offers"].includes(key) ? "sm:col-span-2" : ""}>
                <span className="mb-1 block text-xs font-semibold text-neutral-600">{label}</span>
                <input
                  value={profile[key] || ""}
                  onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
                  data-testid={`profile-${key.replace(/_/g, "-")}`}
                  className={inputCls}
                />
              </label>
            ))}
          </div>
          <div className="mt-4 space-y-3">
            <div>
              <span className="mb-1.5 block text-xs font-semibold text-neutral-600">Marketing goals</span>
              <ChipGroup testid="profile-goal" options={GOAL_OPTIONS} values={profile.goals || []} onChange={(v) => setProfile({ ...profile, goals: v })} />
            </div>
            <div>
              <span className="mb-1.5 block text-xs font-semibold text-neutral-600">Preferred channels <span className="font-normal text-neutral-400">(preferences only — no external accounts are connected)</span></span>
              <ChipGroup testid="profile-channel" options={CHANNEL_OPTIONS} values={profile.channels || []} onChange={(v) => setProfile({ ...profile, channels: v })} />
            </div>
          </div>
          <Btn className="mt-4" data-testid="profile-save-button" disabled={savingProfile} onClick={saveProfile}>
            {savingProfile ? "Saving…" : "Save profile"}
          </Btn>
        </Card>

        {/* Step 2 — generate */}
        <Card className="flex flex-col p-6" data-testid="strategy-generate-card">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Step 2 — Generate your strategy</p>
          <p className="mt-3 text-sm leading-relaxed text-neutral-500">
            The AI reads your profile once and produces a nine-section strategy: overview, audience,
            positioning, channels, content pillars, a 30-day plan, lead generation, budget split and KPIs.
          </p>
          {approved && (
            <div className="mt-4">
              <Notice testid="strategy-approved-note" title="Approved strategy in place"
                body={`Approved ${String(approved.approved_at || "").slice(0, 10)}. Generating a new draft never replaces it.`} />
            </div>
          )}
          <div className="mt-auto pt-5">
            <Btn data-testid="strategy-generate-button" disabled={generating} onClick={generate}>
              <Sparkles size={14} className="mr-1.5 inline" />
              {generating ? "Generating — this takes up to a minute…" : current ? "Generate revised strategy" : "Generate AI Strategy"}
            </Btn>
            <p className="mt-2 text-[11px] text-neutral-400">Counts once against your monthly AI marketing allowance.</p>
            {strategies.length > 0 && (
              <button className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-[#111111]"
                data-testid="strategy-history-toggle" onClick={() => setShowList(!showList)}>
                <History size={12} /> Previous strategies ({strategies.length})
              </button>
            )}
            {showList && (
              <div className="mt-2 space-y-1" data-testid="strategy-history-list">
                {strategies.map((s) => (
                  <button key={s.id} onClick={() => openStrategy(s.id)} data-testid={`strategy-open-${s.id}`}
                    className="flex w-full items-center justify-between rounded-lg border border-neutral-200 px-3 py-2 text-left text-xs hover:bg-neutral-50">
                    <span>{String(s.created_at).slice(0, 16).replace("T", " ")}</span>
                    <Pill tone={s.status === "approved" ? "green" : s.status === "archived" ? "grey" : "amber"}>{s.status}</Pill>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Step 3-5 — review, edit, approve */}
      {current && (
        <Card className="p-6" data-testid="strategy-review-card">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Step 3 — Review &amp; edit</p>
              <Pill tone={current.status === "approved" ? "green" : current.status === "archived" ? "grey" : "amber"}>{current.status}</Pill>
            </div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="strategy-save-button" disabled={!dirty || savingStrategy} onClick={saveStrategy}>
                {savingStrategy ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </Btn>
              {current.status !== "approved" && (
                <Btn data-testid="strategy-approve-button" onClick={approve}>
                  <CheckCircle2 size={14} className="mr-1.5 inline" /> Approve strategy
                </Btn>
              )}
              {current.status === "draft" && (
                <Btn variant="ghost" data-testid="strategy-archive-button" onClick={async () => {
                  try {
                    const { data } = await api.post(`/marketing/strategies/${current.id}/archive`);
                    setCurrent(data);
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                }}>
                  <Archive size={14} className="mr-1 inline" /> Archive
                </Btn>
              )}
            </div>
          </div>
          <p className="mb-4 text-[11px] leading-relaxed text-neutral-400">
            AI-generated recommendations — review before relying on them. Approval accepts the strategy only; nothing is published, emailed or advertised automatically.
          </p>
          <div className="space-y-4">
            {SECTION_ORDER.map((key) => (
              <div key={key}>
                <span className="mb-1 block text-xs font-bold text-neutral-700">{SECTION_LABELS[key]}</span>
                <textarea
                  rows={Math.min(10, Math.max(3, (drafts?.[key] || "").split("\n").length + 1))}
                  value={drafts?.[key] || ""}
                  disabled={current.status === "archived"}
                  onChange={(e) => { setDrafts({ ...drafts, [key]: e.target.value }); setDirty(true); }}
                  data-testid={`strategy-section-${key}`}
                  className={`${inputCls} whitespace-pre-wrap leading-relaxed`}
                />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
